import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FileText, ArrowRight,
  Globe, Zap, Shield, TrendingUp, Users, Lightbulb, Briefcase, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import MarketingHeader from '@/components/MarketingHeader';
import MarketingFooter from '@/components/MarketingFooter';
import SEO from '@/components/SEO';

const guides = [
  {
    title: 'Website Launch Checklist',
    description: 'Everything you need to verify before launching your new website. From technical SEO to security checks.',
    category: 'Web Development',
    icon: Globe,
    gradient: 'from-blue-500 to-indigo-500',
    path: '/resources/guides/website-launch'
  },
  {
    title: 'Small Business AI Starter Guide',
    description: 'A practical introduction to AI tools that can actually help your small business save time and money.',
    category: 'AI & Automation',
    icon: Zap,
    gradient: 'from-violet-500 to-purple-500',
    path: '/resources/guides/ai-starter'
  },
  {
    title: 'Website Security Essentials',
    description: 'Protect your business online with this guide to essential security measures every website needs.',
    category: 'Security',
    icon: Shield,
    gradient: 'from-emerald-500 to-teal-500',
    path: '/resources/guides/security-essentials'
  },
  {
    title: 'Local SEO Playbook',
    description: 'Step-by-step guide to ranking higher in local search results and getting more customers through your door.',
    category: 'SEO & Marketing',
    icon: TrendingUp,
    gradient: 'from-orange-500 to-amber-500',
    path: '/resources/guides/local-seo'
  },
  {
    title: 'Client Onboarding Playbook',
    description: 'Create a seamless onboarding experience that sets the tone for successful client relationships.',
    category: 'Business Operations',
    icon: Users,
    gradient: 'from-rose-500 to-pink-500',
    path: '/resources/guides/client-onboarding'
  },
  {
    title: 'Digital Transformation Roadmap',
    description: 'A strategic guide to modernizing your business operations with technology that fits your needs.',
    category: 'Strategy',
    icon: Lightbulb,
    gradient: 'from-cyan-500 to-blue-500',
    path: '/resources/guides/digital-transformation'
  },
  {
    title: 'Project Management Guide',
    description: 'A comprehensive guide to managing projects from initiation to closing. Learn the five phases of successful project delivery.',
    category: 'Project Management',
    icon: Briefcase,
    gradient: 'from-indigo-500 to-blue-500',
    path: '/resources/guides/project-management'
  },
  {
    title: 'Change Management Guide',
    description: 'Learn how to lead organizational change using the ADKAR model and overcome resistance to change.',
    category: 'Change Management',
    icon: RefreshCw,
    gradient: 'from-purple-500 to-violet-500',
    path: '/resources/guides/change-management'
  }
];

const guidesSchema = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  "name": "Guides & Playbooks - Fearless IT Resources",
  "description": "Free guides, playbooks, and resources to help businesses grow with technology. Practical, actionable content from the Fearless IT team.",
  "publisher": {
    "@type": "Organization",
    "name": "Fearless IT",
    "url": "https://fearlessit.io"
  }
};

const GuidesPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="guides-page">
      <SEO 
        title="Guides & Playbooks - Free Business Resources"
        description="Free guides, playbooks, and resources to help businesses grow with technology. Website checklists, SEO guides, AI starter guides, and more from Fearless IT."
        keywords="business guides, technology playbooks, website checklist, SEO guide, AI for small business, digital transformation guide, free business resources"
        url="/resources/guides"
        structuredData={guidesSchema}
      />
      
      <MarketingHeader />

      {/* Hero Section */}
      <section className="pt-16 pb-12 sm:pt-20 sm:pb-16 bg-gradient-to-b from-teal-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-teal-100 rounded-full mb-6">
            <FileText className="h-4 w-4 text-teal-600" />
            <span className="text-sm font-medium text-teal-700">Resources</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-carbon-black mb-4" data-testid="guides-title">
            Guides & Playbooks
          </h1>
          <p className="text-xl sm:text-2xl font-semibold mb-6" style={{ background: 'linear-gradient(to right, #0D9488, #14B8A6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Practical Resources for Growing Your Business
          </p>
          <p className="text-lg sm:text-xl text-slate-gray max-w-2xl mx-auto leading-relaxed">
            Free, actionable guides to help you make better technology decisions and grow your business with confidence.
          </p>
        </div>
      </section>

      {/* Free Guides Notice */}
      <section className="py-8 bg-gradient-to-r from-teal-500 to-emerald-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-white font-semibold">
            All guides are free to read. Use them to plan your next big digital change with confidence.
          </p>
        </div>
      </section>

      {/* Guides Grid */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-carbon-black mb-4">
              Business & Technology Guides
            </h2>
            <p className="text-lg text-slate-gray max-w-2xl mx-auto">
              Practical guides created from real experience helping businesses succeed.
            </p>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {guides.map((guide, idx) => {
              const IconComponent = guide.icon;
              return (
                <Link key={idx} to={guide.path} className="block">
                  <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden group hover:shadow-xl transition-shadow h-full">
                    <div className={`h-24 bg-gradient-to-r ${guide.gradient} flex items-center justify-center`}>
                      <IconComponent className="h-10 w-10 text-white opacity-90" />
                    </div>
                    <div className="p-6">
                      <div className="mb-3">
                        <span className="text-xs font-semibold text-teal-600 uppercase tracking-wider">
                          {guide.category}
                        </span>
                      </div>
                      <h3 className="text-lg font-bold text-carbon-black mb-2 group-hover:text-teal-600 transition-colors">
                        {guide.title}
                      </h3>
                      <p className="text-sm text-slate-gray leading-relaxed">
                        {guide.description}
                      </p>
                      <div className="mt-4 flex items-center text-teal-600 font-medium text-sm group-hover:text-teal-700">
                        Read Guide
                        <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Request Custom Guide */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl p-8 sm:p-12 border border-teal-100 text-center">
            <Lightbulb className="h-12 w-12 text-teal-500 mx-auto mb-6" />
            <h2 className="text-2xl sm:text-3xl font-bold text-carbon-black mb-4">
              Need Something Specific?
            </h2>
            <p className="text-lg text-slate-gray mb-8 max-w-xl mx-auto">
              Have a topic you'd like us to cover? Let us know and we'll consider creating a guide for it.
            </p>
            <Link to="/contact">
              <Button className="bg-gradient-to-r from-teal-500 to-emerald-500 text-white px-8 py-6 text-lg font-semibold rounded-xl hover:from-teal-600 hover:to-emerald-600 transition-all">
                Request a Guide
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-teal-50 to-emerald-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-carbon-black mb-4">
            Ready for Personalized Guidance?
          </h2>
          <p className="text-lg text-slate-gray mb-8 max-w-2xl mx-auto">
            Get a customized assessment of your business's digital presence with our free Growth Audit.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/growth-audit">
              <Button className="gradient-fearless text-white px-8 py-6 text-lg font-semibold rounded-xl hover:opacity-90 transition-all w-full sm:w-auto">
                Free Growth Audit
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="px-8 py-6 text-lg font-semibold rounded-xl border-2 border-teal-400 text-teal-600 hover:bg-teal-50 transition-all w-full sm:w-auto">
                Contact Us
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default GuidesPage;
